import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { View, LayoutChangeEvent } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, LinearTransition } from 'react-native-reanimated';
import { WH } from '_utils/dimensions';
import useIsHelpfulNeighbor from '_utils/messages/useIsHelpfulNeighbor';
import { smallestMargin, smallestMarginPixels, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeimeText, SelectorChip } from 'Components';
import RichTextView from 'Components/RichText/RichTextView';
import { HelpfulNeighborTag, HelpfulNeighborTags } from 'types/message';
import { ArrayElement } from 'types/Utility';

const HelpfulNeighborDetails = ({ userId }: { userId: number }) => {
    const { theme } = useThemeContext();
    const helperInfo = useIsHelpfulNeighbor(userId);
    const { t } = useTranslation();
    const [showAll, setShowAll] = useState(false);
    const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
    const [measuredState, setMeasuredState] = useState<'none' | 'expanded' | 'all'>('none');
    const animatedHeight = useSharedValue(0);
    const measureRef = useRef<{
        chips: Record<string, number>;
        containerWidth?: number;
        expandedHeight?: number;
    }>({ chips: {} });

    const chipTypes = helperInfo?.helpful_neighbor_tags ?? [];

    // Layout handlers
    const handleLayout = (event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        measureRef.current.containerWidth = width;
        checkMeasureState();
    };

    const checkMeasureState = useCallback(() => {
        const { containerWidth, chips, expandedHeight } = measureRef.current;
        // All chips and the +n chip are measured
        if (containerWidth && expandedHeight && chips && Object.keys(chips).length === chipTypes.length + 1) {
            if (collapsedHeight == null) {
                setMeasuredState('expanded');
            } else {
                setMeasuredState('all');
            }
        }
    }, [collapsedHeight, chipTypes.length]);

    const handleChipLayout = useCallback(
        (type: string, event: LayoutChangeEvent) => {
            const { width } = event.nativeEvent.layout;
            measureRef.current.chips = {
                ...measureRef.current.chips,
                [type]: width,
            };
            checkMeasureState();
        },
        [checkMeasureState],
    );

    // Calculate visible chips for single line
    const calculateVisibleChips = () => {
        if (measuredState === 'none') {
            return chipTypes;
        }

        if (measureRef.current.containerWidth == null) {
            throw new Error('Container width not measured');
        }

        let currentWidth = 0;
        const visibleChips: ArrayElement<typeof HelpfulNeighborTags>[] = [];
        const plusChipWidth = measureRef.current.chips['+n'] || 0;
        const totalChips = chipTypes.length;
        for (let i = 0; i < totalChips; i++) {
            const type = chipTypes[i];
            const chipWidth = measureRef.current.chips[type];
            const totalWidth = chipWidth + smallestMarginPixels;

            // If not the last chip, check if we need to reserve space for +n
            const remaining = totalChips - (i + 1);
            const needsPlus = remaining > 0;
            const reservedWidth = needsPlus ? plusChipWidth + smallestMarginPixels : 0;
            if (currentWidth + totalWidth + reservedWidth > measureRef.current.containerWidth) {
                break;
            }
            currentWidth += totalWidth;
            visibleChips.push(type);
        }
        return visibleChips;
    };

    const visibleChips = showAll ? chipTypes : calculateVisibleChips();
    const hiddenCount = chipTypes.length - visibleChips.length;
    const showPlus = hiddenCount > 0 || measuredState !== 'all';

    // Height measurement handlers
    const onExpandedLayout = (e: LayoutChangeEvent) => {
        if (measureRef.current.expandedHeight == null) {
            measureRef.current.expandedHeight = e.nativeEvent.layout.height;
            checkMeasureState();
        }
    };
    const onCollapsedLayout = (e: LayoutChangeEvent) => {
        if (collapsedHeight == null) {
            setCollapsedHeight(e.nativeEvent.layout.height);
            setMeasuredState('all');
        }
    };

    // Animate height when both heights are measured
    useEffect(() => {
        if (measuredState === 'all') {
            animatedHeight.value = withTiming((showAll ? measureRef.current.expandedHeight : collapsedHeight) ?? 0, {
                duration: showAll ? 350 : 0,
            });
        }
    }, [showAll, collapsedHeight, animatedHeight, measuredState]);

    // Animated style for container
    const containerStyle = useAnimatedStyle(() => ({
        height: measuredState === 'all' ? animatedHeight.value : undefined,
        overflow: 'hidden',
    }));

    if (!helperInfo) {
        return null;
    }

    // Always render the main ChipsRow, and render hidden measurement rows for collapsed/expanded heights
    return (
        <View>
            <HeimeText
                style={{
                    marginTop: WH * 0.01,
                    fontSize: subtitleFontSize,
                    fontWeight: 'bold',
                    color: theme.black,
                }}
            >
                {t('profile:helpfulNeighbor')}
            </HeimeText>
            {/* Main animated row */}
            <Animated.View
                onLayout={handleLayout}
                style={[
                    {
                        flexDirection: 'row',
                        flexWrap: showAll ? 'wrap' : 'nowrap',
                        gap: smallestMargin,
                    },
                    containerStyle,
                ]}
            >
                <ChipsRow
                    key={measuredState}
                    chipTypes={chipTypes}
                    visibleChips={visibleChips}
                    showAll={showAll}
                    showPlus={showPlus}
                    onChipLayout={handleChipLayout}
                    wrap={measuredState === 'none' || showAll}
                    fadeInStartIdx={visibleChips.length}
                    onPlusPress={() => setShowAll(true)}
                    onContainerLayout={measuredState === 'none' ? onExpandedLayout : onCollapsedLayout}
                />
            </Animated.View>
            <RichTextView
                content={helperInfo.richtext_content ? [helperInfo.richtext_content] : []}
                style={{
                    color: theme.black,
                }}
            />
        </View>
    );
};

const ChipsRow = ({
    chipTypes,
    visibleChips,
    showAll,
    showPlus,
    onChipLayout,
    onContainerLayout,
    wrap,
    fadeInStartIdx,
    onPlusPress,
}: {
    chipTypes: Array<HelpfulNeighborTag>;
    visibleChips: Array<HelpfulNeighborTag>;
    showAll: boolean;
    showPlus: boolean;
    onChipLayout: (type: string, e: LayoutChangeEvent) => void;
    onContainerLayout?: (e: LayoutChangeEvent) => void;
    wrap: boolean;
    fadeInStartIdx?: number;
    onPlusPress?: () => void;
}) => {
    const hiddenCount = chipTypes.length - visibleChips.length;

    return (
        <View
            onLayout={onContainerLayout}
            style={{
                flexDirection: 'row',
                flexWrap: wrap ? 'wrap' : 'nowrap',
                gap: smallestMargin,
                opacity: 1,
                position: 'relative',
            }}
        >
            {(showAll ? chipTypes : visibleChips).map((type, idx) => (
                <AnimatedChip
                    key={type}
                    type={type}
                    onLayout={(e) => onChipLayout(type, e)}
                    fadeIn={showAll && fadeInStartIdx !== undefined && idx >= fadeInStartIdx}
                />
            ))}
            {!showAll && showPlus && (
                <View onLayout={(e) => onChipLayout('+n', e)}>
                    <SelectorChip
                        variant="WhitePrimaryGrey"
                        title={`+${hiddenCount}`}
                        maxFontSizeMultiplier={2}
                        onSelect={onPlusPress}
                    />
                </View>
            )}
        </View>
    );
};

const AnimatedChip = ({
    type,
    onLayout,
    fadeIn,
}: {
    type: HelpfulNeighborTag;
    onLayout: (e: LayoutChangeEvent) => void;
    fadeIn: boolean;
}) => {
    const { t } = useTranslation();
    const style = useAnimatedStyle(
        () => ({
            opacity: fadeIn ? withTiming(1, { duration: 300 }) : 1,
        }),
        [fadeIn],
    );

    return (
        <Animated.View key={type} onLayout={onLayout} style={style} layout={LinearTransition.springify()}>
            <SelectorChip
                variant="WhitePrimaryGrey"
                title={t(`sharingAll:${type}_title`)}
                maxFontSizeMultiplier={2}
                containerStyle={{ marginRight: 0 }}
            />
        </Animated.View>
    );
};

export default HelpfulNeighborDetails;
